import { useEffect, useMemo, useState } from "react";
import "./TeacherHomeScreen.css";
import { connect } from "react-redux";
import AppHeader from "../../../AppHeader";
import { RootState } from "../../../../state/configureStore";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import Loading from "../../../Loading/Loading";
import ReadleeProfile from "../../../../services/firebase/ReadleeProfile";
import InformationModal from "../../../Modals/InformationModal";
import { closeInformationModal, InformationModalState } from "../../../Modals/InformationModalReducer";
import AnalyticsService from "../../../../services/analytics/AnalyticsService";
import TeacherHomeScreenSidebar from "./TeacherHomeScreenSidebar";
import { Drawer, Hidden } from "@material-ui/core";
import UserSettings from "../../../../interfaces/UserSettings";
import TeacherSettings from "../TeacherSettings/TeacherSettings";
import Classroom from "../../../../interfaces/Classroom";
import ClassPage from "../ClassPage/ClassPage";
import ClassroomAssignmentDetails from "../../../../interfaces/ClassroomAssignmentDetails";
import AssignmentsPage from "../AssignmentsPage/AssignmentsPage";
import AssignmentOnTeacher from "../../../../state/teacherData/AssignmentOnTeacher";
import StudentView from "../StudentView/StudentView";
import AssignmentView from "../AssignmentView/AssignmentView";
import SubmissionView from "../SubmissionView/SubmissionView";
import { checkFilterByParam, filterAssignmentsFunc, filterStudents, findClassroom } from "./FilteringFromURLFunctions";
import queryString from "query-string";
import { assignmentSubmissionPath, classSubmissionPath } from "../../../AssignmentReport/BackButton";

interface Students {
  [key: string]: ReadleeProfile;
}

interface Assignments {
  [key: string]: AssignmentOnTeacher;
}

interface AssignmentClasses {
  [key: string]: {
    [key: string]: ClassroomAssignmentDetails;
  };
}

interface ScreenProps {
  isConnectingUser: boolean;
  isLoggedIn: boolean;
  isConnectingSettings: boolean;
  userSettings: UserSettings | undefined;
  profile: ReadleeProfile | undefined;
  isLoadingTeacher: boolean;
  assignments: Assignments;
  starredAssignments: string[];
  assignmentClasses: AssignmentClasses;
  classrooms: Classroom[];
  students: Students;
  informationModalState: InformationModalState;
}

function TeacherHomeScreen(props: ScreenProps & RouteComponentProps) {
  const isConnecting = props.isConnectingUser || props.isConnectingSettings;
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  const filterAssignments = useMemo(
    () => filterAssignmentsFunc(props.assignments, props.assignmentClasses, props.starredAssignments, props.profile),
    [props.assignments, props.assignmentClasses, props.starredAssignments, props.profile],
  );

  useEffect(() => {
    AnalyticsService.logScreenView();
  }, []);

  if (isConnecting || props.isLoadingTeacher) {
    return <Loading />;
  }

  if (!props.profile) {
    return <Route component={() => <Redirect to={{ pathname: "/Login" }} />} render={undefined} />;
  }

  return (
    <div className="fullContainer">
      <AppHeader className="TeacherHeader" toggleSidebar={() => setSidebarOpen(!isSidebarOpen)} />
      <div className="SidebarAndMain">
        <Hidden smDown>
          <TeacherHomeScreenSidebar />
        </Hidden>

        <div className="TeacherHomeScreen">
          <Switch>
            <Route
              exact
              path={`/Class/:classId`}
              render={({ match }) => {
                // find classroom that matches the id in the url
                const routeClassroom = findClassroom(props.classrooms, match.params.classId);
                // if classroom doesn't exist, show 'not found'
                // if it exists, show the classPage
                return !routeClassroom ? (
                  <p>Classroom not found</p>
                ) : (
                  <ClassPage
                    classroom={routeClassroom}
                    students={filterStudents(routeClassroom, props.students)}
                    assignments={filterAssignments(undefined, match.params.classId)}
                    assignmentClasses={props.assignmentClasses}
                  />
                );
              }}
            />
            <Route exact path="/Class/:classId/Student/:studentId">
              <StudentView />
            </Route>
            <Route path={[classSubmissionPath, assignmentSubmissionPath]}>
              <SubmissionView />
            </Route>
            <Route
              exact
              path={`/Assignments`}
              render={({ location }) => {
                const parsed = queryString.parse(location.search);
                const filterBy =
                  checkFilterByParam(parsed.filterBy) && typeof parsed.filterBy === "string"
                    ? parsed.filterBy
                    : undefined;
                const classId = typeof parsed.classId === "string" ? parsed.classId : undefined;
                return (
                  <AssignmentsPage
                    assignments={filterAssignments(filterBy, classId)}
                    assignmentClasses={props.assignmentClasses}
                    classrooms={props.classrooms}
                  />
                );
              }}
            />
            <Route path="/Assignments/:assignmentId">
              <AssignmentView />
            </Route>
            <Route
              path="/Settings"
              render={() => <TeacherSettings userSettings={props.userSettings} profile={props.profile} />}
            />
            <Route exact path="/">
              <Redirect to={props.classrooms.length > 0 ? `/Class/${props.classrooms?.[0]?.id}` : `/`} />
            </Route>
          </Switch>
        </div>
      </div>
      <Hidden mdUp>
        <Drawer
          open={isSidebarOpen}
          onClose={() => {
            setSidebarOpen(false);
          }}
        >
          <Switch>
            <Route>
              <TeacherHomeScreenSidebar />
            </Route>
          </Switch>
        </Drawer>
      </Hidden>
      <InformationModal
        title={props.informationModalState.title}
        onOk={closeInformationModal}
        open={props.informationModalState.isOpen}
      >
        {props.informationModalState.message}
      </InformationModal>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  profile: state.currentUserState.currentUser,
  isLoadingTeacher: state.teacherState.isLoadingTeacher,
  classrooms: state.teacherState.classrooms,
  assignments: state.teacherState.assignments,
  starredAssignments: state.teacherState.starredAssignments,
  assignmentClasses: state.teacherState.assignmentClasses,
  students: state.teacherState.students,
  isConnectingUser: state.currentUserState.isConnectingUser,
  isLoggedIn: state.currentUserState.currentUser !== undefined,
  isConnectingSettings: state.currentUserState.isConnectingSettings,
  userSettings: state.currentUserState.currentUserSettings,
  informationModalState: state.informationModalState,
});

export default connect(mapStateToProps)(TeacherHomeScreen);
